import { Editor, Transforms, Text, Element as SlateElement } from 'slate'
import { jsx } from 'slate-hyperscript'
import escapeHtml from 'escape-html'

const LIST_TYPES = ['numbered-list', 'bulleted-list']

export const MyEditor = {
  isFormatActive(editor, format) {
    const [match] = Editor.nodes(editor, {
      match: (n) => n[format] === true,
      mode: 'all',
    })
    return !!match
  },
  toggleFormat(editor, format) {
    const isActive = MyEditor.isFormatActive(editor, format)
    Transforms.setNodes(
      editor,
      { [format]: isActive ? null : true },
      { match: Text.isText, split: true }
    )
  },
  // toggleMark(editor, format) {
  //   const isActive = MyEditor.isMarkActive(editor, format)

  //   if (isActive) {
  //     Editor.removeMark(editor, format)
  //   } else {
  //     Editor.addMark(editor, format, true)
  //   }
  // },
  isBlockActive(editor, format) {
    const { selection } = editor
    if (!selection) return false

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n['type'] === format,
      })
    )

    return !!match
  },
  toggleBlock(editor, format) {
    const isActive = MyEditor.isBlockActive(editor, format)
    const isList = LIST_TYPES.includes(format)

    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        LIST_TYPES.includes(n.type),
      split: true,
    })
    let newProperties
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
    Transforms.setNodes(editor, newProperties)

    if (!isActive && isList) {
      const block = { type: format, children: [] }
      Transforms.wrapNodes(editor, block)
    }
  },
  serialize(node, disabledParagraph = false) {
    if (Text.isText(node)) {
      let string = escapeHtml(node.text)
      if (node.bold) {
        string = `<strong>${string}</strong>`
      }
      if (node.italic) {
        string = `<em>${string}</em>`
      }
      if (node.underlined) {
        string = `<u>${string}</u>`
      }
      return string
    }
    const children = node.children
      .map((n) => MyEditor.serialize(n, disabledParagraph))
      .join('')
    const className = node.className ? ` class="${node.className}"` : ''
    switch (node.type) {
      case 'div':
        return !disabledParagraph
          ? `<p${className}>${children}</p>`
          : `<div${className}>${children}</div>`
      case 'span':
        return `<span${className}>${children}</span>`
      case 'paragraph':
        return !disabledParagraph
          ? `<p${className}>${children}</p>`
          : `<div${className}>${children}</div>`
      case 'h1':
        return `<h1${className}>${children}</h1>`
      case 'h2':
        return `<h2${className}>${children}</h2>`
      case 'h3':
        return `<h3${className}>${children}</h3>`
      case 'h4':
        return `<h4${className}>${children}</h4>`
      case 'h5':
        return `<h5${className}>${children}</h5>`
      case 'h6':
        return `<h6${className}>${children}</h6>`
      case 'numbered-list':
        return `<ol${className}>${children}</ol>`
      case 'bulleted-list':
        return `<ul${className}>${children}</ul>`
      case 'list-item':
        return `<li${className}>${children}</li>`
      case 'link':
        return `<a${className} href="${escapeHtml(
          node.url
        )}" rel="noreferrer" target="_blank">${children}</a>`
      case 'strong':
        return `<strong${className}>${children}</strong>`
      case 'em':
        return `<em${className}>${children}</em>`
      case 'u':
        return `<u${className}>${children}</u>`
      default:
        return children
    }
  },
  deserialize(el, markAttributes = {}) {
    if (el.nodeType === Node.TEXT_NODE) {
      return jsx('text', markAttributes, el.textContent)
    } else if (el.nodeType !== Node.ELEMENT_NODE) {
      return null
    }

    const nodeAttributes = { ...markAttributes }

    // define attributes for text nodes
    switch (el.nodeName) {
      case 'STRONG':
        nodeAttributes.bold = true
        break
      case 'EM':
        nodeAttributes.italic = true
        break
      case 'U':
        nodeAttributes.underlined = true
        break
    }

    const children = Array.from(el.childNodes)
      .map((node) => MyEditor.deserialize(node, nodeAttributes))
      .flat()

    if (children.length === 0) {
      children.push(jsx('text', nodeAttributes, ''))
    }

    switch (el.nodeName) {
      case 'BODY':
        return jsx('fragment', {}, children)
      case 'DIV':
        return jsx('element', { type: 'div' }, children)
      case 'BR':
        return '\n'
      case 'BLOCKQUOTE':
        return jsx('element', { type: 'quote' }, children)
      case 'P':
        return jsx('element', { type: 'paragraph' }, children)
      case 'A':
        return jsx(
          'element',
          { type: 'link', url: el.getAttribute('href') },
          children
        )
      case 'OL':
        return jsx('element', { type: 'numbered-list' }, children)
      case 'UL':
        return jsx('element', { type: 'bulleted-list' }, children)
      case 'LI':
        return jsx(
          'element',
          {
            type: 'list-item',
          },
          children
        )
      case 'H1':
      case 'H2':
      case 'H3':
      case 'H4':
      case 'H5':
      case 'H6': {
        return jsx(
          'element',
          {
            type: `${el.nodeName.toLowerCase()[0]}${el.nodeName[1]}`,
          },
          children
        )
      }
      default:
        return children
    }
  },
}
